import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend,
} from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

/** Multi-dataset series for grouped/stacked bars */
export interface BarSeries {
  label: string
  data: number[]
  color: string
}

interface Props {
  labels: string[]
  // ── Single-series (original API — backward-compatible) ──
  data?: number[]
  colors?: string[]
  color?: string
  // ── Multi-series grouped bars ──
  series?: BarSeries[]
  stacked?: boolean
  // ── Common ──
  height?: number | string
  horizontal?: boolean
  yTickFormat?: (v: number) => string
  xTickFormat?: (v: number) => string
  barThickness?: number
  /** Hard cap on the y-axis maximum. Useful to prevent a single outlier day
   *  from compressing all other bars to near-zero. Tooltip still shows the
   *  real value even when the bar is clipped. */
  yMax?: number
}

export function BarChart({
  labels,
  data, colors, color = '#10b981',
  series,
  stacked = false,
  height = 260, horizontal = false,
  yTickFormat, xTickFormat,
  barThickness = 28,
  yMax,
}: Props) {
  // Build datasets — multi-series takes priority over single-series
  const datasets = series
    ? series.map(s => ({
        label: s.label,
        data: s.data,
        backgroundColor: s.color,
        borderRadius: 4,
        barThickness,
      }))
    : [{
        data: data ?? [],
        backgroundColor: colors ?? labels.map(() => color),
        borderRadius: 6,
        barThickness,
      }]

  const isMulti  = Boolean(series)
  const tickFont = { family: 'JetBrains Mono' } as const

  return (
    <div style={{ height, width: '100%' }}>
      <Bar
        data={{ labels, datasets }}
        options={{
          indexAxis: horizontal ? 'y' : 'x',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: isMulti
              ? { display: true, position: 'bottom', labels: { usePointStyle: true, boxWidth: 6, padding: 12, font: { size: 11 } } }
              : { display: false },
            tooltip: {
              callbacks: {
                label: (c) => {
                  const v   = c.parsed[horizontal ? 'x' : 'y'] ?? 0
                  const fmt = horizontal ? xTickFormat : yTickFormat
                  const lbl = c.dataset.label ? c.dataset.label + ': ' : ''
                  return lbl + (fmt ? fmt(v) : v.toLocaleString())
                },
              },
            },
          },
          scales: {
            y: {
              stacked: stacked || undefined,
              max: yMax,
              ticks: {
                // For horizontal charts the y-axis is the categorical axis and
                // receives the tick INDEX — return the label string at that index.
                // For vertical charts the y-axis is the value axis — apply yTickFormat.
                callback: (v) => horizontal
                  ? (labels[Number(v)] ?? String(v))
                  : (yTickFormat ? yTickFormat(Number(v)) : String(v)),
                font: tickFont,
              },
              grid: { color: '#eef2f7', display: !horizontal },
              display: !horizontal ? true : undefined,
            },
            x: {
              stacked: stacked || undefined,
              ticks: {
                // For categorical x-axes Chart.js passes the tick INDEX as `v`,
                // not the label string. Return the label at that index for
                // vertical charts; apply xTickFormat to the value for horizontal.
                callback: (v) => horizontal
                  ? (xTickFormat ? xTickFormat(Number(v)) : String(v))
                  : (labels[Number(v)] ?? String(v)),
                font: { weight: 600 },
              },
              grid: { display: false },
            },
          },
        }}
      />
    </div>
  )
}
